import {
  FileText,
  FileCheck,
  Sparkles,
  FileOutput,
  Inbox,
  ShoppingCart,
  Truck,
  RotateCcw,
  Receipt,
  Package,
  FileInput,
  ClipboardCheck,
  Wrench,
  ClipboardList,
  BarChart3,
  ArrowLeftRight,
  Repeat,
  ArrowRight,
  Layers,
  Fuel,
  CreditCard,
  AlertTriangle,
  Camera,
  CalendarClock,
  Gauge,
  Gavel,
  Zap,
  PieChart,
  PiggyBank,
  Landmark,
  PhoneCall,
  FileWarning,
  HandCoins,
  Banknote,
  CircleSlash,
} from 'lucide-react';
import type { ElementType } from 'react';
import type { DocumentType } from '@/hooks/useDocumentsWorkItems';

export type FinanceDocumentNavType = DocumentType | 'proposal' | 'recurring' | 'invoice_batches' | 'sales_inbox' | 'expense' | 'procurement_field_docs';
export type FinanceDocumentNavGroup = 'sales' | 'purchases' | 'warehouse' | 'expenses' | 'property';

export interface FinanceDocumentHeaderConfig {
  docType: FinanceDocumentNavType;
  tabId: string;
  singular: string;
  plural: string;
  icon: ElementType;
  primaryColor: string;
  badgeColor: string;
  hoverColor: string;
  subtitle?: string;
  group: FinanceDocumentNavGroup;
  /** Subtle per-tab accent color (hex). When set, this tints the header slightly. */
  accentColor?: string;
  /** When true, a visual separator is shown before this tab in the header strip */
  separatorBefore?: boolean;
  /** Which header row the tab belongs to. 'primary' renders in the main pill bar,
   *  'secondary' renders in a second pill bar below the title. Defaults to 'primary'. */
  row?: 'primary' | 'secondary';
}

// ============================================================================
// DYNAMIC PALETTE GENERATION
// Generates harmonious per-tab accent colors from a base module color.
// Each document group gets a palette: slight hue shifts + lightness variations
// so all tabs feel like siblings of the same module color.
// ============================================================================

function hexToHsl(hex: string): [number, number, number] {
  const r = parseInt(hex.slice(1, 3), 16) / 255;
  const g = parseInt(hex.slice(3, 5), 16) / 255;
  const b = parseInt(hex.slice(5, 7), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const l = (max + min) / 2;
  if (max === min) return [0, 0, l * 100];
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h = 0;
  if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  else if (max === g) h = ((b - r) / d + 2) / 6;
  else h = ((r - g) / d + 4) / 6;
  return [h * 360, s * 100, l * 100];
}

function hslToHex(h: number, s: number, l: number): string {
  h = ((h % 360) + 360) % 360;
  s = Math.max(0, Math.min(100, s)) / 100;
  l = Math.max(0, Math.min(100, l)) / 100;
  const a = s * Math.min(l, 1 - l);
  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const c = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * Math.max(0, Math.min(1, c))).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
}

/**
 * Generate N accent colors from a base hex color.
 * Colors shift in hue by small amounts and vary lightness slightly,
 * keeping all colors in the same family.
 */
export function generateDocumentPalette(baseHex: string, count: number): string[] {
  const [baseH, baseS, baseL] = hexToHsl(baseHex);
  const hueRange = Math.min(40, count * 4); // ±20° max spread
  const lightnessRange = 12; // ±6% lightness
  const result: string[] = [];

  for (let i = 0; i < count; i++) {
    const t = count <= 1 ? 0 : (i / (count - 1)) - 0.5; // -0.5 to +0.5
    const hShift = t * hueRange;
    const lShift = t * lightnessRange;
    result.push(hslToHex(baseH + hShift, baseS, Math.max(25, Math.min(55, baseL + lShift))));
  }
  return result;
}

// Default palette for sales documents (14 tabs) based on #b83976
const SALES_PALETTE = generateDocumentPalette('#b83976', 14);
// Default palette for purchases (8 tabs) based on #1e5daa (blue family)
const PURCHASES_PALETTE = generateDocumentPalette('#1e5daa', 8);
// Default palette for warehouse (4 tabs) based on #0f766e (teal family)
const WAREHOUSE_PALETTE = generateDocumentPalette('#0f766e', 4);
// Property finance tabs based on #0d9488 (property-finance module teal)
const PROPERTY_PALETTE = generateDocumentPalette('#0d9488', 14);

function withIdentityColor(config: FinanceDocumentHeaderConfig): FinanceDocumentHeaderConfig {
  // Keep the config's hand-set primaryColor — palette colors are applied per-group below
  return config;
}

export const FINANCE_DOCUMENT_HEADER_CONFIG: Record<FinanceDocumentNavType, FinanceDocumentHeaderConfig> = {
  invoice: withIdentityColor({
    docType: 'invoice',
    tabId: 'invoices',
    singular: 'Invoice',
    plural: 'Invoices',
    icon: FileText,
    primaryColor: '#be2f7b',
    badgeColor: 'bg-fuchsia-600',
    hoverColor: 'hover:bg-fuchsia-700',
    subtitle: 'Manage your invoices',
    group: 'sales',
  }),
  quote: withIdentityColor({
    docType: 'quote',
    tabId: 'quotes',
    singular: 'Quote',
    plural: 'Quotations',
    icon: FileCheck,
    primaryColor: '#a6358f',
    badgeColor: 'bg-purple-600',
    hoverColor: 'hover:bg-purple-700',
    subtitle: 'Manage your quotations',
    group: 'sales',
  }),
  proposal: withIdentityColor({
    docType: 'proposal',
    tabId: 'proposals',
    singular: 'Proposal',
    plural: 'Proposals',
    icon: Sparkles,
    primaryColor: '#db2777',
    badgeColor: 'bg-pink-600',
    hoverColor: 'hover:bg-pink-700',
    subtitle: 'Manage AI-generated and standalone proposals',
    group: 'sales',
  }),
  quote_request: withIdentityColor({
    docType: 'quote_request',
    tabId: 'quote-requests',
    singular: 'Quote Request',
    plural: 'Quote Requests',
    icon: ClipboardCheck,
    primaryColor: '#d97706',
    badgeColor: 'bg-amber-600',
    hoverColor: 'hover:bg-amber-700',
    subtitle: 'Field quote requests awaiting pricing',
    group: 'sales',
  }),
  billing_submission: withIdentityColor({
    docType: 'billing_submission',
    tabId: 'billing-submissions',
    singular: 'Billing Submission',
    plural: 'Billing Submissions',
    icon: ClipboardCheck,
    primaryColor: '#0f766e',
    badgeColor: 'bg-teal-600',
    hoverColor: 'hover:bg-teal-700',
    subtitle: 'Review operations billing packs before finance conversion',
    group: 'sales',
  }),
  proforma: withIdentityColor({
    docType: 'proforma',
    tabId: 'proforma',
    singular: 'Pro-Forma Invoice',
    plural: 'Pro-Forma',
    icon: FileOutput,
    primaryColor: '#cc4b8d',
    badgeColor: 'bg-pink-500',
    hoverColor: 'hover:bg-pink-600',
    subtitle: 'Manage your pro-forma invoices',
    group: 'sales',
  }),
  sales_order: withIdentityColor({
    docType: 'sales_order',
    tabId: 'sales-orders',
    singular: 'Sales Order',
    plural: 'Sales Orders',
    icon: ShoppingCart,
    primaryColor: '#8f3fb9',
    badgeColor: 'bg-violet-600',
    hoverColor: 'hover:bg-violet-700',
    subtitle: 'Manage your sales orders',
    group: 'sales',
  }),
  work_order: withIdentityColor({
    docType: 'work_order',
    tabId: 'work-orders',
    singular: 'Work Order',
    plural: 'Work Orders',
    icon: Wrench,
    primaryColor: '#7e3af2',
    badgeColor: 'bg-indigo-600',
    hoverColor: 'hover:bg-indigo-700',
    subtitle: 'Manage your work orders',
    group: 'sales',
  }),
  delivery_note: withIdentityColor({
    docType: 'delivery_note',
    tabId: 'delivery-notes',
    singular: 'Delivery Note',
    plural: 'Delivery Notes',
    icon: Truck,
    primaryColor: '#d94678',
    badgeColor: 'bg-rose-500',
    hoverColor: 'hover:bg-rose-600',
    subtitle: 'Manage your delivery notes',
    group: 'sales',
  }),
  waybill: withIdentityColor({
    docType: 'waybill',
    tabId: 'waybills',
    singular: 'Waybill',
    plural: 'Waybills',
    icon: Truck,
    primaryColor: '#f59e0b',
    badgeColor: 'bg-amber-500',
    hoverColor: 'hover:bg-amber-600',
    subtitle: 'Manage your shipment waybills',
    group: 'sales',
  }),
  credit_note: withIdentityColor({
    docType: 'credit_note',
    tabId: 'credit-notes',
    singular: 'Credit Note',
    plural: 'Credit Notes',
    icon: RotateCcw,
    primaryColor: '#9d2d62',
    badgeColor: 'bg-pink-700',
    hoverColor: 'hover:bg-pink-800',
    subtitle: 'Manage your credit notes',
    group: 'sales',
  }),
  debit_note: withIdentityColor({
    docType: 'debit_note',
    tabId: 'debit-notes',
    singular: 'Debit Note',
    plural: 'Debit Notes',
    icon: FileText,
    primaryColor: '#7f1d5a',
    badgeColor: 'bg-fuchsia-800',
    hoverColor: 'hover:bg-fuchsia-900',
    subtitle: 'Manage your debit notes',
    group: 'sales',
  }),
  rma: withIdentityColor({
    docType: 'rma',
    tabId: 'rma',
    singular: 'Return Authorization',
    plural: 'Returns (RMA)',
    icon: ArrowRight,
    primaryColor: '#6d28d9',
    badgeColor: 'bg-purple-700',
    hoverColor: 'hover:bg-purple-800',
    subtitle: 'Manage your returns and authorizations',
    group: 'sales',
  }),
  recurring: withIdentityColor({
    docType: 'recurring',
    tabId: 'recurring',
    singular: 'Recurring Schedule',
    plural: 'Recurring',
    icon: Repeat,
    primaryColor: '#9333ea',
    badgeColor: 'bg-violet-600',
    hoverColor: 'hover:bg-violet-700',
    subtitle: 'Manage your recurring documents',
    group: 'sales',
  }),
  sales_inbox: withIdentityColor({
    docType: 'sales_inbox',
    tabId: 'sales-inbox',
    singular: 'Sales Inbox',
    plural: 'Sales Inbox',
    icon: Inbox,
    primaryColor: '#be2f7b',
    badgeColor: 'bg-pink-600',
    hoverColor: 'hover:bg-pink-700',
    subtitle: 'Review inbound customer sales documents before conversion',
    group: 'sales',
  }),
  invoice_batches: withIdentityColor({
    docType: 'invoice_batches',
    tabId: 'invoice-batches',
    singular: 'Invoice Batch',
    plural: 'Invoice Batches',
    icon: Layers,
    primaryColor: '#be2f7b',
    badgeColor: 'bg-fuchsia-700',
    hoverColor: 'hover:bg-fuchsia-800',
    subtitle: 'Group invoices for review and approval',
    group: 'sales',
  }),
  supplier_quote: withIdentityColor({
    docType: 'supplier_quote',
    tabId: 'supplier-quotes',
    singular: 'Supplier Quote',
    plural: 'Supplier Quotes',
    icon: FileCheck,
    primaryColor: '#2563eb',
    badgeColor: 'bg-blue-600',
    hoverColor: 'hover:bg-blue-700',
    subtitle: 'Manage supplier quotations',
    group: 'purchases',
  }),
  supplier_invoice: withIdentityColor({
    docType: 'supplier_invoice',
    tabId: 'supplier-invoices',
    singular: 'Supplier Bill',
    plural: 'Supplier Bills',
    icon: Receipt,
    primaryColor: '#1e40af',
    badgeColor: 'bg-blue-800',
    hoverColor: 'hover:bg-blue-900',
    subtitle: 'Manage supplier bills',
    group: 'purchases',
  }),
  supplier_delivery_note: withIdentityColor({
    docType: 'supplier_delivery_note',
    tabId: 'supplier-delivery-notes',
    singular: 'Supplier Delivery Note',
    plural: 'Supplier Delivery Notes',
    icon: Truck,
    primaryColor: '#1e40af',
    badgeColor: 'bg-blue-800',
    hoverColor: 'hover:bg-blue-900',
    subtitle: 'Manage supplier delivery notes',
    group: 'purchases',
  }),
  purchase_order: withIdentityColor({
    docType: 'purchase_order',
    tabId: 'purchase-orders',
    singular: 'Purchase Order',
    plural: 'Purchase Orders',
    icon: ClipboardList,
    primaryColor: '#1d4ed8',
    badgeColor: 'bg-indigo-600',
    hoverColor: 'hover:bg-indigo-700',
    subtitle: 'Manage purchase orders',
    group: 'purchases',
  }),
  procurement_field_docs: withIdentityColor({
    docType: 'procurement_field_docs',
    tabId: 'procurement-field-docs',
    singular: 'Field Document',
    plural: 'Field Documents',
    icon: Camera,
    primaryColor: '#0891b2',
    badgeColor: 'bg-cyan-600',
    hoverColor: 'hover:bg-cyan-700',
    subtitle: 'Field worker procurement document submissions',
    group: 'purchases',
    row: 'secondary',
  }),
  grn: withIdentityColor({
    docType: 'grn',
    tabId: 'goods-received',
    singular: 'Goods Received Note',
    plural: 'Goods Received',
    icon: Package,
    primaryColor: '#0f766e',
    badgeColor: 'bg-teal-600',
    hoverColor: 'hover:bg-teal-700',
    subtitle: 'Manage goods received notes',
    group: 'purchases',
  }),

  supplier_credit: withIdentityColor({
    docType: 'supplier_credit',
    tabId: 'supplier-credits',
    singular: 'Supplier Credit',
    plural: 'Supplier Credits',
    icon: FileInput,
    primaryColor: '#155e75',
    badgeColor: 'bg-cyan-700',
    hoverColor: 'hover:bg-cyan-800',
    subtitle: 'Manage supplier credits',
    group: 'purchases',
  }),
  rfq: withIdentityColor({
    docType: 'rfq',
    tabId: 'rfq',
    singular: 'Request for Quote',
    plural: 'Requests for Quote',
    icon: ClipboardCheck,
    primaryColor: '#0369a1',
    badgeColor: 'bg-sky-700',
    hoverColor: 'hover:bg-sky-800',
    subtitle: 'Manage RFQs',
    group: 'purchases',
  }),
  purchase_return: withIdentityColor({
    docType: 'purchase_return',
    tabId: 'purchase-returns',
    singular: 'Purchase Return',
    plural: 'Purchase Returns',
    icon: RotateCcw,
    primaryColor: '#1e3a8a',
    badgeColor: 'bg-blue-900',
    hoverColor: 'hover:bg-blue-950',
    subtitle: 'Manage purchase returns',
    group: 'purchases',
  }),
  expense: withIdentityColor({
    docType: 'expense',
    tabId: 'expenses',
    singular: 'Adhoc Expense',
    plural: 'Adhoc Expenses',
    icon: Receipt,
    primaryColor: '#059669',
    badgeColor: 'bg-emerald-600',
    hoverColor: 'hover:bg-emerald-700',
    subtitle: 'Manage adhoc business expenses',
    group: 'purchases',
  }),
  purchase_requisition: withIdentityColor({
    docType: 'purchase_requisition',
    tabId: 'purchase-requisitions',
    singular: 'Purchase Requisition',
    plural: 'Purchase Requisitions',
    icon: ClipboardCheck,
    primaryColor: '#3b82f6',
    badgeColor: 'bg-blue-500',
    hoverColor: 'hover:bg-blue-600',
    subtitle: 'Manage purchase requisitions',
    group: 'purchases',
  }),
  picking_slip: withIdentityColor({
    docType: 'picking_slip',
    tabId: 'picking-slips',
    singular: 'Picking Slip',
    plural: 'Picking Slips',
    icon: ClipboardList,
    primaryColor: '#0f766e',
    badgeColor: 'bg-emerald-600',
    hoverColor: 'hover:bg-emerald-700',
    subtitle: 'Manage picking slips',
    group: 'warehouse',
  }),
  packing_slip: withIdentityColor({
    docType: 'packing_slip',
    tabId: 'packing-slips',
    singular: 'Packing Slip',
    plural: 'Packing Slips',
    icon: Package,
    primaryColor: '#0d9488',
    badgeColor: 'bg-teal-500',
    hoverColor: 'hover:bg-teal-600',
    subtitle: 'Manage packing slips',
    group: 'warehouse',
  }),
  stock_adjustment: withIdentityColor({
    docType: 'stock_adjustment',
    tabId: 'stock-adjustments',
    singular: 'Stock Adjustment',
    plural: 'Stock Adjustments',
    icon: BarChart3,
    primaryColor: '#0f766e',
    badgeColor: 'bg-cyan-600',
    hoverColor: 'hover:bg-cyan-700',
    subtitle: 'Manage stock adjustments',
    group: 'warehouse',
  }),
  transfer_order: withIdentityColor({
    docType: 'transfer_order',
    tabId: 'transfer-orders',
    singular: 'Transfer Order',
    plural: 'Transfer Orders',
    icon: ArrowLeftRight,
    primaryColor: '#115e59',
    badgeColor: 'bg-teal-700',
    hoverColor: 'hover:bg-teal-800',
    subtitle: 'Manage transfer orders',
    group: 'warehouse',
  }),
  // Additional expense types
  purchase: withIdentityColor({
    docType: 'purchase',
    tabId: 'purchases',
    singular: 'Purchase',
    plural: 'Purchases',
    icon: ShoppingCart,
    primaryColor: '#f59e0b',
    badgeColor: 'bg-amber-600',
    hoverColor: 'hover:bg-amber-700',
    subtitle: 'Manage purchases',
    group: 'purchases',
  }),
  din: withIdentityColor({
    docType: 'din',
    tabId: 'document-intake',
    singular: 'Document Intake',
    plural: 'Document Intakes',
    icon: Inbox,
    primaryColor: '#6366f1',
    badgeColor: 'bg-indigo-600',
    hoverColor: 'hover:bg-indigo-700',
    subtitle: 'Review ingested documents',
    group: 'expenses',
  }),
  // ── Property Finance ──
  levy_cycle: withIdentityColor({
    docType: 'levy_cycle',
    tabId: 'levy-cycles',
    singular: 'Levy Cycle',
    plural: 'Levy Cycles',
    icon: CalendarClock,
    primaryColor: '#0d9488',
    badgeColor: 'bg-teal-600',
    hoverColor: 'hover:bg-teal-700',
    subtitle: 'Monthly and special-levy billing runs per scheme',
    group: 'property',
  }),
  meter_reading: withIdentityColor({
    docType: 'meter_reading',
    tabId: 'meter-readings',
    singular: 'Meter Reading',
    plural: 'Meter Readings',
    icon: Gauge,
    primaryColor: '#0284c7',
    badgeColor: 'bg-sky-600',
    hoverColor: 'hover:bg-sky-700',
    subtitle: 'Meter readings feeding consumption billing',
    group: 'property',
  }),
  utility_recovery: withIdentityColor({
    docType: 'utility_recovery',
    tabId: 'utility-recoveries',
    singular: 'Utility Recovery',
    plural: 'Utility Recoveries',
    icon: Zap,
    primaryColor: '#d97706',
    badgeColor: 'bg-amber-600',
    hoverColor: 'hover:bg-amber-700',
    subtitle: 'Municipal rates and utilities recovery runs',
    group: 'property',
  }),
  special_levy: withIdentityColor({
    docType: 'special_levy',
    tabId: 'special-levies',
    singular: 'Special Levy',
    plural: 'Special Levies',
    icon: Gavel,
    primaryColor: '#7c3aed',
    badgeColor: 'bg-violet-600',
    hoverColor: 'hover:bg-violet-700',
    subtitle: 'Special levy resolutions and collections',
    group: 'property',
  }),
  scheme_budget: withIdentityColor({
    docType: 'scheme_budget',
    tabId: 'scheme-budgets',
    singular: 'Scheme Budget',
    plural: 'Scheme Budgets',
    icon: PieChart,
    primaryColor: '#4f46e5',
    badgeColor: 'bg-indigo-600',
    hoverColor: 'hover:bg-indigo-700',
    subtitle: 'Scheme budgets under the management rules',
    group: 'property',
  }),
  reserve_fund: withIdentityColor({
    docType: 'reserve_fund',
    tabId: 'reserve-funds',
    singular: 'Reserve Fund Plan',
    plural: 'Reserve Fund Plans',
    icon: PiggyBank,
    primaryColor: '#059669',
    badgeColor: 'bg-emerald-600',
    hoverColor: 'hover:bg-emerald-700',
    subtitle: 'Ten-year reserve fund plans',
    group: 'property',
  }),
  deposit_holding: withIdentityColor({
    docType: 'deposit_holding',
    tabId: 'deposit-holdings',
    singular: 'Deposit Holding',
    plural: 'Deposit Holdings',
    icon: Landmark,
    primaryColor: '#0369a1',
    badgeColor: 'bg-sky-700',
    hoverColor: 'hover:bg-sky-800',
    subtitle: 'Tenant deposits held in trust with interest',
    group: 'property',
  }),
  trust_transaction: withIdentityColor({
    docType: 'trust_transaction',
    tabId: 'trust-transactions',
    singular: 'Trust Transaction',
    plural: 'Trust Transactions',
    icon: Receipt,
    primaryColor: '#0f766e',
    badgeColor: 'bg-teal-600',
    hoverColor: 'hover:bg-teal-700',
    subtitle: 'Trust account receipts, payments and interest entries',
    group: 'property',
  }),
  payment_dispute: withIdentityColor({
    docType: 'payment_dispute',
    tabId: 'payment-disputes',
    singular: 'Payment Dispute',
    plural: 'Payment Disputes',
    icon: AlertTriangle,
    primaryColor: '#dc2626',
    badgeColor: 'bg-red-600',
    hoverColor: 'hover:bg-red-700',
    subtitle: 'Payment disputes under investigation',
    group: 'property',
  }),
  payment_plan: withIdentityColor({
    docType: 'payment_plan',
    tabId: 'payment-plans',
    singular: 'Payment Plan',
    plural: 'Payment Plans',
    icon: HandCoins,
    primaryColor: '#16a34a',
    badgeColor: 'bg-green-600',
    hoverColor: 'hover:bg-green-700',
    subtitle: 'Arrears payment arrangements',
    group: 'property',
  }),
  collection_case: withIdentityColor({
    docType: 'collection_case',
    tabId: 'collection-cases',
    singular: 'Collection Case',
    plural: 'Collection Cases',
    icon: PhoneCall,
    primaryColor: '#b45309',
    badgeColor: 'bg-amber-700',
    hoverColor: 'hover:bg-amber-800',
    subtitle: 'Levy arrears collection cases',
    group: 'property',
  }),
  invoice_followup: withIdentityColor({
    docType: 'invoice_followup',
    tabId: 'invoice-followups',
    singular: 'Invoice Follow-Up',
    plural: 'Invoice Follow-Ups',
    icon: FileWarning,
    primaryColor: '#c2410c',
    badgeColor: 'bg-orange-600',
    hoverColor: 'hover:bg-orange-700',
    subtitle: 'Outstanding invoice follow-ups',
    group: 'property',
  }),
  credit_request: withIdentityColor({
    docType: 'credit_request',
    tabId: 'credit-requests',
    singular: 'Credit Request',
    plural: 'Credit Requests',
    icon: Banknote,
    primaryColor: '#0891b2',
    badgeColor: 'bg-cyan-600',
    hoverColor: 'hover:bg-cyan-700',
    subtitle: 'Credit requests pending approval',
    group: 'property',
  }),
  write_off: withIdentityColor({
    docType: 'write_off',
    tabId: 'write-offs',
    singular: 'Write-Off',
    plural: 'Write-Offs',
    icon: CircleSlash,
    primaryColor: '#6b7280',
    badgeColor: 'bg-gray-600',
    hoverColor: 'hover:bg-gray-700',
    subtitle: 'Bad-debt write-off requests',
    group: 'property',
  }),
};

export const SALES_DOCUMENT_HEADER_TABS: FinanceDocumentHeaderConfig[] = [
  // --- Primary row: core sales documents ---
  { ...FINANCE_DOCUMENT_HEADER_CONFIG.quote, accentColor: SALES_PALETTE[0], row: 'primary' },
  { ...FINANCE_DOCUMENT_HEADER_CONFIG.proforma, accentColor: SALES_PALETTE[3], row: 'primary' },
  { ...FINANCE_DOCUMENT_HEADER_CONFIG.sales_order, accentColor: SALES_PALETTE[4], row: 'primary' },
  { ...FINANCE_DOCUMENT_HEADER_CONFIG.work_order, accentColor: SALES_PALETTE[5], row: 'primary' },
  { ...FINANCE_DOCUMENT_HEADER_CONFIG.invoice, accentColor: SALES_PALETTE[8], separatorBefore: true, row: 'primary' },
  { ...FINANCE_DOCUMENT_HEADER_CONFIG.credit_note, accentColor: SALES_PALETTE[10], row: 'primary' },
  { ...FINANCE_DOCUMENT_HEADER_CONFIG.debit_note, accentColor: SALES_PALETTE[11], row: 'primary' },

  // --- Secondary row: supporting / fulfillment documents ---
  { ...FINANCE_DOCUMENT_HEADER_CONFIG.quote_request, accentColor: SALES_PALETTE[2], row: 'secondary' },
  { ...FINANCE_DOCUMENT_HEADER_CONFIG.sales_inbox, accentColor: SALES_PALETTE[9], row: 'secondary' },
  { ...FINANCE_DOCUMENT_HEADER_CONFIG.delivery_note, accentColor: SALES_PALETTE[6], row: 'secondary' },
  { ...FINANCE_DOCUMENT_HEADER_CONFIG.waybill, accentColor: SALES_PALETTE[7], row: 'secondary' },
  { ...FINANCE_DOCUMENT_HEADER_CONFIG.rma, accentColor: SALES_PALETTE[12], row: 'secondary' },
  { ...FINANCE_DOCUMENT_HEADER_CONFIG.recurring, accentColor: SALES_PALETTE[13] ?? '#9333ea', row: 'secondary' },
];

export const PURCHASE_DOCUMENT_HEADER_TABS: FinanceDocumentHeaderConfig[] = [
  // --- Primary row: core purchasing documents ---
  { ...FINANCE_DOCUMENT_HEADER_CONFIG.rfq, accentColor: PURCHASES_PALETTE[0], row: 'primary' },
  { ...FINANCE_DOCUMENT_HEADER_CONFIG.supplier_quote, accentColor: PURCHASES_PALETTE[1], row: 'primary' },
  { ...FINANCE_DOCUMENT_HEADER_CONFIG.purchase_order, accentColor: PURCHASES_PALETTE[3], row: 'primary' },
  { ...FINANCE_DOCUMENT_HEADER_CONFIG.expense, accentColor: '#10b981', row: 'primary' },

  // --- Secondary row: supporting documents ---
  { ...FINANCE_DOCUMENT_HEADER_CONFIG.purchase_requisition, accentColor: PURCHASES_PALETTE[2], row: 'secondary' },
  { ...FINANCE_DOCUMENT_HEADER_CONFIG.grn, accentColor: PURCHASES_PALETTE[4], row: 'secondary' },
  { ...FINANCE_DOCUMENT_HEADER_CONFIG.supplier_invoice, accentColor: PURCHASES_PALETTE[5], row: 'secondary' },
  { ...FINANCE_DOCUMENT_HEADER_CONFIG.supplier_credit, accentColor: PURCHASES_PALETTE[6], row: 'secondary' },
  { ...FINANCE_DOCUMENT_HEADER_CONFIG.purchase_return, accentColor: PURCHASES_PALETTE[7], row: 'secondary' },
];

export const EXPENSE_DOCUMENT_HEADER_TABS: FinanceDocumentHeaderConfig[] = [
  { ...FINANCE_DOCUMENT_HEADER_CONFIG.expense, accentColor: '#10b981', row: 'primary' },
  { ...FINANCE_DOCUMENT_HEADER_CONFIG.supplier_invoice, accentColor: '#1e40af', row: 'primary' },
];

export const WAREHOUSE_DOCUMENT_HEADER_TABS: FinanceDocumentHeaderConfig[] = [
  { ...FINANCE_DOCUMENT_HEADER_CONFIG.picking_slip, accentColor: WAREHOUSE_PALETTE[0] },
  { ...FINANCE_DOCUMENT_HEADER_CONFIG.packing_slip, accentColor: WAREHOUSE_PALETTE[1] },
  { ...FINANCE_DOCUMENT_HEADER_CONFIG.stock_adjustment, accentColor: WAREHOUSE_PALETTE[2] },
  { ...FINANCE_DOCUMENT_HEADER_CONFIG.transfer_order, accentColor: WAREHOUSE_PALETTE[3] },
];

// Property Finance document tabs — one entry per property-finance doc type,
// ordered to mirror the property-finance.yaml sub_pages. Surfaces pick the
// subset for their active sub-page (budgets and arrears are multi-doc tabs).
export const PROPERTY_FINANCE_DOCUMENT_HEADER_TABS: FinanceDocumentHeaderConfig[] = [
  { ...FINANCE_DOCUMENT_HEADER_CONFIG.levy_cycle, accentColor: PROPERTY_PALETTE[0] },
  { ...FINANCE_DOCUMENT_HEADER_CONFIG.meter_reading, accentColor: PROPERTY_PALETTE[1] },
  { ...FINANCE_DOCUMENT_HEADER_CONFIG.utility_recovery, accentColor: PROPERTY_PALETTE[2] },
  { ...FINANCE_DOCUMENT_HEADER_CONFIG.special_levy, accentColor: PROPERTY_PALETTE[3] },
  { ...FINANCE_DOCUMENT_HEADER_CONFIG.scheme_budget, accentColor: PROPERTY_PALETTE[4] },
  { ...FINANCE_DOCUMENT_HEADER_CONFIG.reserve_fund, accentColor: PROPERTY_PALETTE[5] },
  { ...FINANCE_DOCUMENT_HEADER_CONFIG.deposit_holding, accentColor: PROPERTY_PALETTE[6] },
  { ...FINANCE_DOCUMENT_HEADER_CONFIG.trust_transaction, accentColor: PROPERTY_PALETTE[7] },
  { ...FINANCE_DOCUMENT_HEADER_CONFIG.payment_dispute, accentColor: PROPERTY_PALETTE[8] },
  { ...FINANCE_DOCUMENT_HEADER_CONFIG.payment_plan, accentColor: PROPERTY_PALETTE[9] },
  { ...FINANCE_DOCUMENT_HEADER_CONFIG.collection_case, accentColor: PROPERTY_PALETTE[10] },
  { ...FINANCE_DOCUMENT_HEADER_CONFIG.invoice_followup, accentColor: PROPERTY_PALETTE[11] },
  { ...FINANCE_DOCUMENT_HEADER_CONFIG.credit_request, accentColor: PROPERTY_PALETTE[12] },
  { ...FINANCE_DOCUMENT_HEADER_CONFIG.write_off, accentColor: PROPERTY_PALETTE[13] },
];

export function getFinanceDocumentHeaderConfig(docType: FinanceDocumentNavType) {
  return FINANCE_DOCUMENT_HEADER_CONFIG[docType];
}
